import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Terminal } from "lucide-react";

const LINES = [
  { text: "pip install flintly", comment: false },
  { text: "flintly claude", comment: false },
  { text: "# proxy listening on 127.0.0.1:8120", comment: true },
  { text: "# context compression: active", comment: true },
];

export const Install = () => {
  const [copied, setCopied] = useState(false);

  const copyCommands = async () => {
    try {
      await navigator.clipboard.writeText("pip install flintly\nflintly claude");
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed", { description: "Select the commands manually." });
    }
  };

  return (
    <section
      data-testid="install-section"
      className="max-w-6xl mx-auto px-6 py-24 border-b border-seam"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-ember">
            Get Running
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mt-1 mb-4">
            Two commands. Zero config.
          </h2>
          <p className="text-sm text-smoke leading-relaxed mb-6 max-w-md">
            Flintly wraps the agent you already use. Install the CLI, prefix
            your agent command, and every session runs through the compression
            proxy — your workflow does not change, your token bill does.
          </p>
          <ul className="space-y-3 text-xs text-smoke">
            {[
              "Works with Claude Code, Cursor, Aider, Gemini CLI, Codex and OpenCode",
              "Runs entirely on localhost — code never leaves your machine",
              "Original context recoverable on demand via MCP",
            ].map((point) => (
              <li key={point} className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-ember mt-1.5 shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-6">
          <div className="rounded-xl border border-seam bg-black/60 overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-seam bg-coal">
              <div className="flex items-center gap-2 text-xs text-smoke">
                <Terminal className="w-3.5 h-3.5 text-ember" />
                <span className="font-mono">terminal</span>
              </div>
              <button
                onClick={copyCommands}
                data-testid="install-copy-button"
                className="flex items-center gap-1.5 text-[11px] text-smoke hover:text-ember transition-colors"
              >
                {copied ? (
                  <Check className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="p-5 font-mono text-sm space-y-2">
              {LINES.map((line) => (
                <div key={line.text} className="flex gap-3">
                  <span className="text-ember select-none">
                    {line.comment ? "" : "$"}
                  </span>
                  <span className={line.comment ? "text-smoke" : "text-white"}>
                    {line.text}
                  </span>
                </div>
              ))}
              <div className="flex gap-3 pt-1">
                <span className="text-ember select-none">$</span>
                <span className="w-2 h-4 bg-ember/80 animate-pulse" />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-smoke mt-3 text-center">
            Free for individuals. No account required to start.
          </p>
        </div>
      </div>
    </section>
  );
};
